package org.zest.insan_360_website.data.page_states.dashboard

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.model.*
import org.zest.insan_360_website.pages.user.students_layout.InviteState
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.getSearchScore
import org.zest.insan_360_website.utils.processResponse


object StudentsDashboardState { // students management
    var invitesListState by mutableStateOf<AdminStudentsInvitePayload?>(null)
    var studentsInfoState by mutableStateOf<AdminStudentsInfoPayload?>(null)
    var inviteState by mutableStateOf(InviteState())
    var isLoadingInvite by mutableStateOf(false)
    var isLoadingInfo by mutableStateOf(false)
    var needUpdate by mutableStateOf(true)

    var searchQuery by mutableStateOf("")

    var isInviting by mutableStateOf(false)
    var isConfirmingInvite by mutableStateOf(false)
    var inviteEmail by mutableStateOf("")

    fun reset() {
        invitesListState = null
        studentsInfoState = null
        inviteState = InviteState()
        isLoadingInvite = false
        isLoadingInfo = false
        needUpdate = true
        searchQuery = ""
        isInviting = false
        isConfirmingInvite = false
        inviteEmail = ""
    }

    fun getFilteredMergedList(testStudents: List<Student> = emptyList(), testInvites: List<StudentInvite> = emptyList()): List<Triple<Student?, StudentInvite?, Double>> {
        val students = studentsInfoState?.students ?: emptyList()
        val invites = invitesListState?.invites ?: emptyList()
        
        val allStudentsBase = students + testStudents
        val allInvites = invites + testInvites
        
        val mergedMap = mutableMapOf<String, Pair<Student?, StudentInvite?>>()
        
        allStudentsBase.forEach { s ->
            mergedMap[s.email] = Pair(s, null)
        }
        allInvites.forEach { i ->
            val existing = mergedMap[i.email]
            mergedMap[i.email] = Pair(existing?.first, i)
        }
        
        val mergedList = mergedMap.values.map { (s, i) ->
            val sortTime = i?.invitedAt?.toEpochMilliseconds()?.toDouble() ?: 0.0
            Triple(s, i, sortTime)
        }.sortedByDescending { it.third }
        
        return if (searchQuery.isBlank()) {
            mergedList
        } else {
            mergedList.mapNotNull { (s, i, time) ->
                val nameTarget = s?.name ?: i?.name ?: ""
                val emailTarget = s?.email ?: i?.email ?: ""
                
                val nameScore = if (nameTarget.isNotBlank()) getSearchScore(searchQuery, nameTarget) else Int.MAX_VALUE
                val emailScore = if (emailTarget.isNotBlank()) getSearchScore(searchQuery, emailTarget) else Int.MAX_VALUE
                
                val bestScore = minOf(nameScore, emailScore)
                
                if (bestScore <= 130) Pair(Triple(s, i, time), bestScore) else null
            }.sortedBy { it.second }
             .map { it.first }
        }
    }

    fun fetchInvitesList(scope: CoroutineScope) {
        val role = AuthState.role
        if (role != UserRole.ADMIN) return
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        isLoadingInvite = true

        scope.launch {
            try {
                val request = AdminRequest(
                    email = email,
                    role = role,
                    request = AdminStudentsDashRequest(
                        email = email
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/admin/dashboard/invites", request)
                    ?: throw Exception("Session expired. Please log in again.")

                invitesListState = processResponse(response, fallbackErrorLog = "Error fetching invites")

            } catch (e: Exception) {
                console.error(e)
            } finally {
                isLoadingInvite = false
            }
        }
    }

    fun fetchStudentsInfoList(scope: CoroutineScope) {
        val role = AuthState.role
        if (role != UserRole.ADMIN) return
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        isLoadingInfo = true

        scope.launch {
            try {
                val request = AdminRequest(
                    email = email,
                    role = role,
                    request = AdminStudentsDashRequest(
                        email
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/admin/dashboard/students", request)
                    ?: throw Exception("Session expired. Please log in again.")

                studentsInfoState = processResponse(response, fallbackErrorLog = "Error loading students info")

            } catch (e: Exception) {
                console.error(e)
            } finally {
                isLoadingInfo = false
            }
        }
    }

    fun sendInvite(scope: CoroutineScope, toast: ToastState, onFinished: () -> Unit) {
        val role = AuthState.role
        if (role != UserRole.ADMIN) return
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        inviteState = inviteState.copy(isSending = true)
        toast.show("Sending invite...")

        scope.launch {
            try {
                val request = AdminRequest(
                    email = email,
                    role = role,
                    request = AdminInviteRequest(
                        inviter = email,
                        invitee = inviteState.email
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/admin/invite", request)
                    ?: throw Exception("Session expired. Please log in again.")

                processResponse<Unit>(response, toast, "Failed to send invite.")

            } catch (e: Exception) {
                console.error("Network error sending invite: ${e.message}")
                toast.show(
                    e.message ?: LanguageSettings.General.networkFailure.localized,
                    org.zest.insan_360_website.components.widgets.ToastType.ERROR
                )
            } finally {
                onFinished()
                inviteState = inviteState.copy(isSending = false)
            }

        }
    }

    fun assignCredit(toAssignId: String, scope: CoroutineScope, toast: ToastState, onFinished: () -> Unit) {
        val role = AuthState.role
        if (role != UserRole.ADMIN) return
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        toast.show("Assigning credit...")

        scope.launch {
            try {
                val request = AdminRequest(
                    email = email,
                    role = role,
                    request = AdminAssignCreditRequest(
                        email = email,
                        studentId = toAssignId
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/admin/assign", request)
                    ?: throw Exception("Session expired. Please log in again.")

                processResponse<Unit>(response, toast, "Failed to assign credit.") { onFinished() }

            } catch (e: Exception) {
                console.error("Network error assigning credit ${e.message}")
            }
        }
    }
}